import { BrowserDataCacheSourceAdapter } from '../core/source-adapter';
import { BrowserDataCacheSubject } from '../core/subject';
import { BrowserDataCacheRuntimeCandidate } from '../core/runtime';
import {
  BrowserDataCacheCapabilities,
  BrowserDataCacheQuery,
  BrowserDataCacheQueryResult,
  BrowserDataCacheSyncJob,
  BrowserDataCacheSyncOptions,
  BrowserDataCacheSyncProgress,
  BrowserDataCacheSyncResult,
} from '../core/types';

export interface LiveNoCacheRuntimeOptions {
  sources: BrowserDataCacheSourceAdapter<any, any, any>[];
  jobIdFactory?: () => string;
}

export class LiveNoCacheRuntime {
  readonly capabilities: BrowserDataCacheCapabilities = {
    runtime: 'live-no-cache',
    persistent: false,
    sql: false,
    views: false,
    materializedViews: false,
    backgroundWorker: false,
  };

  private readonly sources = new Map<string, BrowserDataCacheSourceAdapter<any, any, any>>();
  private readonly rowsBySource = new Map<string, any[]>();
  private readonly jobIdFactory: () => string;

  constructor(options: LiveNoCacheRuntimeOptions) {
    for (const source of options.sources || []) {
      this.sources.set(source.id, source);
      this.rowsBySource.set(source.id, []);
    }
    this.jobIdFactory = options.jobIdFactory || (() => `sync-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  }

  startSync(sourceId: string, options: BrowserDataCacheSyncOptions = {}): BrowserDataCacheSyncJob {
    const source = this.getSource(sourceId);
    const progress = new BrowserDataCacheSubject<BrowserDataCacheSyncProgress>();
    const controller = new AbortController();
    const signal = mergeAbortSignals(controller.signal, options.signal);
    const jobId = this.jobIdFactory();
    let cancelReason = '';

    const done = this.runSync(source, options, signal, progress)
      .catch((error) => {
        if (isAbortError(error)) {
          progress.next({
            sourceId,
            phase: 'cancelled',
            statusText: cancelReason || 'Sync cancelled',
          });
          return {
            sourceId,
            rowsRead: 0,
            rowsWritten: 0,
            cancelled: true,
          };
        }
        progress.next({
          sourceId,
          phase: 'failed',
          error: String(error?.message || error || 'Sync failed'),
        });
        throw error;
      });

    return {
      id: jobId,
      sourceId,
      progress$: progress,
      done,
      cancel: (reason?: string) => {
        cancelReason = reason || 'Sync cancelled';
        controller.abort(cancelReason);
      },
    };
  }

  async query<TRow = unknown>(query: BrowserDataCacheQuery): Promise<BrowserDataCacheQueryResult<TRow>> {
    const sourceId = query.sourceId || this.findSourceIdByTable(query.table || query.view || '');
    if (!sourceId) {
      return { rows: [], total: 0, capabilities: this.capabilities };
    }

    const source = this.getSource(sourceId);
    const allRows = this.rowsBySource.get(sourceId) || [];
    const filtered = source.shouldIncludeRow
      ? allRows.filter((row) => source.shouldIncludeRow?.(row, { where: query.where }))
      : filterRowsByWhere(allRows, query.where);
    const offset = Math.max(0, Number(query.offset || 0));
    const limit = query.limit == null ? filtered.length : Math.max(0, Number(query.limit));
    return {
      rows: filtered.slice(offset, offset + limit) as TRow[],
      total: filtered.length,
      capabilities: this.capabilities,
    };
  }

  private async runSync(
    source: BrowserDataCacheSourceAdapter<any, any, any>,
    options: BrowserDataCacheSyncOptions,
    signal: AbortSignal,
    progress: BrowserDataCacheSubject<BrowserDataCacheSyncProgress>,
  ): Promise<BrowserDataCacheSyncResult> {
    const rows: any[] = [];
    let rowsRead = 0;
    let cursor: any = null;
    let shouldContinue = true;
    const stopModifiedAt = normalizeTime(options.stopWhen?.modifiedBeforeOrEqual);

    progress.next({ sourceId: source.id, phase: 'queued', statusText: 'Queued' });
    await source.beforeSync?.(options);

    while (shouldContinue) {
      throwIfAborted(signal);
      progress.next({ sourceId: source.id, phase: 'fetching', completed: rowsRead, statusText: 'Fetching page' });
      const page = await source.listPage({ signal }, cursor);
      const items = page.items || [];
      progress.next({ sourceId: source.id, phase: 'mapping', completed: rowsRead, total: page.total });

      for (const item of items) {
        throwIfAborted(signal);
        if (stopModifiedAt != null && source.getModifiedAt) {
          const modifiedAt = normalizeTime(source.getModifiedAt(item));
          if (modifiedAt != null && modifiedAt <= stopModifiedAt) {
            shouldContinue = false;
            break;
          }
        }
        rowsRead++;
        const row = source.mapRow(item);
        if (row) {
          rows.push(row);
        }
      }

      cursor = page.nextCursor ?? null;
      if (!cursor) {
        shouldContinue = false;
      }
    }

    throwIfAborted(signal);
    progress.next({ sourceId: source.id, phase: 'writing', completed: rowsRead, total: rowsRead, statusText: 'Updating session cache' });
    this.rowsBySource.set(source.id, rows);
    const result = {
      sourceId: source.id,
      rowsRead,
      rowsWritten: rows.length,
      cancelled: false,
    };
    await source.afterSync?.(result);
    progress.next({ sourceId: source.id, phase: 'completed', completed: rowsRead, total: rowsRead, statusText: 'Completed' });
    return result;
  }

  async upsertRows(_table: string, _rows: unknown[]): Promise<void> {
    // live-no-cache holds no persistent state; upserts are no-ops.
  }

  private getSource(sourceId: string): BrowserDataCacheSourceAdapter<any, any, any> {
    const source = this.sources.get(sourceId);
    if (!source) {
      throw new Error(`Unknown browser data cache source: ${sourceId}`);
    }
    return source;
  }

  private findSourceIdByTable(tableOrView: string): string | null {
    if (!tableOrView) return null;
    for (const source of this.sources.values()) {
      if (source.table === tableOrView) {
        return source.id;
      }
    }
    return null;
  }
}

export function createLiveNoCacheRuntimeCandidate(options: LiveNoCacheRuntimeOptions): BrowserDataCacheRuntimeCandidate {
  return {
    id: 'live-no-cache',
    probe: () => ({
      ok: true,
      capabilities: {
        runtime: 'live-no-cache',
        persistent: false,
        sql: false,
        views: false,
        materializedViews: false,
        backgroundWorker: false,
      },
    }),
    create: () => new LiveNoCacheRuntime(options),
  };
}

function filterRowsByWhere(rows: any[], where?: Record<string, unknown>): any[] {
  const entries = Object.entries(where || {});
  if (!entries.length) return rows;
  return rows.filter((row) => entries.every(([key, value]) => row?.[key] === value));
}

function normalizeTime(value: string | number | Date | null | undefined): number | null {
  if (value == null || value === '') return null;
  const time = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('Sync aborted', 'AbortError');
  }
}

function isAbortError(error: any): boolean {
  return error?.name === 'AbortError';
}

function mergeAbortSignals(primary: AbortSignal, secondary?: AbortSignal): AbortSignal {
  if (!secondary) return primary;
  if (secondary.aborted) {
    const controller = new AbortController();
    controller.abort(secondary.reason);
    return controller.signal;
  }
  const controller = new AbortController();
  const abort = () => controller.abort(primary.reason || secondary.reason);
  primary.addEventListener('abort', abort, { once: true });
  secondary.addEventListener('abort', abort, { once: true });
  return controller.signal;
}
